import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { ApplicationConfigurationService } from "@app/core/services/ApplicationConfiguration.service";

export interface ParticipantRelationship { relatedParticipantId: number; relationshipCode: string; }
export interface ParticipantMailingAddress { line1: string; line2: string; city: string; region: string; postalCode: string; country: string; }
export interface NoticeParticipant {
  officialUserId?: number; isPresiding?: boolean;
  id: number; displayName: string; firstName?: string | null; lastName?: string | null; roleCode: string; email: string; postalAddress: string; phone: string;
  isActive: boolean; version: number; relationships: ParticipantRelationship[]; mailingAddress?: ParticipantMailingAddress | null;
}
export interface ParticipantNotice {
  hasServiceAttempts?: boolean;
  id: number; participantId: number; onBehalfOfParticipantId: number | null; recipientName: string;
  statusCode?: string; version?: number; sentUtc?: string | null; deliveredUtc?: string | null; returnedUtc?: string | null; replacesNoticeId?: number | null;
  onBehalfOfName: string; channelCode: string; contactAddress: string; title: string; body: string; generatedUtc: string; templateKey?: string | null; templateVersion?: number | null;
}
export interface ParticipantResponse {
  id: number; participantId: number; onBehalfOfParticipantId: number | null; noticeId: number | null;
  respondentName: string; onBehalfOfName: string; receivedUtc: string; channelCode: string;
  responseCode: string; notes: string; recordedByUserId: number | null; recordedUtc: string | null;
}
export interface ParticipantNoticeTemplate {
  versionId: number; templateKey: string; displayName: string; versionNumber: number; title: string;
  introductoryText: string; importantInformation: string; assistanceText: string; instructions: string;
}
export interface NoticeWorkspace { participants: NoticeParticipant[]; notices: ParticipantNotice[]; responses: ParticipantResponse[]; template?: ParticipantNoticeTemplate | null; }
export interface GenerateNoticeInput { participantId: number; onBehalfOfParticipantId: number | null; channelCode: string; templateVersionId: number; replacesNoticeId?: number; expectedVersion?: number; }
export interface RecordResponseInput {
  participantId: number; onBehalfOfParticipantId: number | null; noticeId: number | null;
  receivedUtc: string; channelCode: string; responseCode: string; notes: string;
}
const options = { withCredentials: true, headers: new HttpHeaders({ "skip-error-interceptor": "yes", "skip-loading-interceptor": "yes" }) };

@Injectable({ providedIn: "root" })
export class HearingNoticeWorkspaceService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ApplicationConfigurationService);
  private url(id: number): string { return this.config.apiUrl(`/api/phms/scheduled-proceedings/${id}/participant-notices`); }
  get(id: number) { return this.http.get<NoticeWorkspace>(this.url(id), options); }
  saveParticipant(id: number, person: NoticeParticipant) {
    return person.id
      ? this.http.put<NoticeParticipant>(`${this.url(id)}/participants/${person.id}`, person, options)
      : this.http.post<NoticeParticipant>(`${this.url(id)}/participants`, person, options);
  }
  generate(id: number, input: GenerateNoticeInput) { return this.http.post<ParticipantNotice>(`${this.url(id)}/notices`, input, options); }
  updateNoticeStatus(id: number, noticeId: number, statusCode: string, expectedVersion: number) {
    return this.http.put<ParticipantNotice>(`${this.url(id)}/notices/${noticeId}/status`, { statusCode, expectedVersion }, options);
  }
  respond(id: number, input: RecordResponseInput) { return this.http.post<ParticipantResponse>(`${this.url(id)}/responses`, input, options); }
  download(id: number, noticeId: number) { return this.http.get(`${this.url(id)}/notices/${noticeId}/pdf`, { ...options, responseType: "blob" }); }
}
